import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Flexer } from '../app/dashboard/elements/LayoutElements';
import { Icon } from '../concepts/resources/Icon';
import { useWindowEvent } from '../platform/Hook';
import { Menu } from './Menu';

export type PickerItem = {
    id: string,
    icon?: string,
    label?: string,
    name?: string,
    text?: string,
};

export type PickerMenuProps<T extends PickerItem = PickerItem> = {
    allowKeyboard?: boolean,
    items: T[],
    renderItem?: (item: T, index: number) => React.ReactNode,
    onAction: (tm: T) => void,
};

const defaultRenderItem = <T extends PickerItem>(item: T, index: number) => <>
    {item.icon && <Icon size="1.25" src={item.icon} />}
    {item.text && <Menu.ItemTextCropped>{item.text}</Menu.ItemTextCropped>}
    {item.name && <Menu.ItemTextCropped>{item.name}</Menu.ItemTextCropped>}
    {item.label && <Menu.ItemTextCropped>{item.label}</Menu.ItemTextCropped>}
</>;

export const PickerMenu = <T extends PickerItem>({ allowKeyboard, items, renderItem, onAction }: PickerMenuProps<T>) => {
    renderItem = renderItem ?? defaultRenderItem;
    const [index, setIndex] = useState(0);
    const [autoScroll, setAutoScroll] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);

    const setIndexWithInput = (nextIndex: number, input: 'keyboard' | 'mouse') => {
        setAutoScroll(input === 'keyboard');
        setIndex(nextIndex);
    };

    useEffect(() => {
        setIndex(0);
    }, [items]);

    useEffect(() => {
        if (!containerRef.current) return;
        if (!autoScroll) return;
        const item = containerRef.current.querySelector(`[data-menu-index="${index}"]`);
        if (!item) return;
        item.scrollIntoView({ block: 'nearest' });
    }, [index, containerRef.current]);

    useWindowEvent('keydown', e => {
        if (!allowKeyboard) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            setIndexWithInput(Math.min(index + 1, items.length - 1), 'keyboard');
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            setIndexWithInput(Math.max(index - 1, 0), 'keyboard');
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            onAction(items[index]);
        }
    });

    useEffect(() => {
        // If keyboard and focus is not set to an input, set it to the container
        const active = document.activeElement as HTMLElement;
        if (active && active.tagName === 'INPUT') return;
        if (active && active.tagName === 'TEXTAREA') return;
        if (active && active.isContentEditable) return;
        if (allowKeyboard) {
            containerRef.current?.focus();
        }
    }, [allowKeyboard, containerRef.current]);

    return <HideMouseUntilMoved enabled={!!allowKeyboard}>
        <Flexer ref={containerRef} minWidth="12" tabIndex={allowKeyboard ? 0 : -1}>
            <Menu>
                {items.map((item, i) =>
                    <Menu.Item
                        key={item.id}
                        data-menu-index={i}
                        highlight={index === i}
                        onMouseMove={() => setIndexWithInput(i, 'mouse')}
                        onClick={() => onAction(item)}>
                        {renderItem(item, i)}
                    </Menu.Item>
                )}
            </Menu>
        </Flexer>
    </HideMouseUntilMoved>;
};

const HideMouse = styled.div<{ active: boolean }>`
    ${p => p.active && css`
        cursor: none !important;
        & * {
            cursor: none !important;
            pointer-events: none !important;
        }
    `}
`;

const HideMouseUntilMoved = ({ enabled, children }: { enabled: boolean, children: React.ReactNode }) => {
    const [hidden, setHidden] = useState(true);
    return <HideMouse active={hidden && enabled} onMouseMove={() => setHidden(false)}>
        {children}
    </HideMouse>;
};
